import React from 'react'
import { makeObservable, observable } from 'mobx'
import { Channel, Group, Ref } from '~/models'
import { dataStore } from '~/stores'
import { GroupField } from '~/ui/app/fields'
import { observer } from '~/ui/component'
import { SwitchField, TextBlock, VBox } from '~/ui/components'
import { FormDialog, FormField, FormModel, SubmitResult, translateFormErrorPaths } from '~/ui/form'
import { useResourceTranslation } from '~/ui/resources'
import { layout } from '~/ui/styling'

export interface Props {
  open:         boolean
  requestClose: () => any

  channel: Channel
}

const ChannelModerationForm = observer('ChannelModerationForm', (props: Props) => {

  const {open, requestClose, channel} = props

  const formModel = React.useMemo(
    () => new ChannelModerationFormModel(channel),
    [channel],
  )

  const {t} = useResourceTranslation('channels')

  //------
  // Rendering

  function render() {
    return (
      <FormDialog
        open={open}
        requestClose={requestClose}
        model={formModel}

        icon='headset'
        title={t('moderation.title')}
        children={renderForm()}
      />
    )
  }

  function renderForm() {
    return (
      <VBox gap={layout.padding.s}>
        <TextBlock small dim>
          {t('moderation.preamble')}
        </TextBlock>

        <FormField name='enabled' caption={false} i18nKey='moderation'>
          {bind => <SwitchField {...bind}/>}
        </FormField>

        {formModel.enabled && (
          <FormField name='moderatorGroup' required>
            {bind => <GroupField {...bind} multi={false}/>}
          </FormField>
        )}
      </VBox>
    )
  }

  return render()

})

export default ChannelModerationForm


class ChannelModerationFormModel implements FormModel {

  constructor(
    private readonly channel: Channel,
  ) {
    makeObservable(this)
  }

  @observable
  public enabled: boolean = this.channel.moderation != null

  @observable
  public moderatorGroup: Ref<Group> | null = this.channel.moderation?.moderatorGroup ?? null

  public async submit(): Promise<SubmitResult | undefined> {
    const update = this.buildUpdate()
    const result = await dataStore.update(Channel, this.channel.id, {moderation: update})
    return translateFormErrorPaths(result, path => path.replace(/^moderation\./, ''))
  }

  private buildUpdate() {
    if (!this.enabled) { return null }

    return {
      moderatorGroup: this.moderatorGroup,
    }
  }

}